// frontend/src/farm/starSky.mjs
// The real night sky over Warsaw for tonight's date, seen facing south (the sun rises on the
// left and sets on the right in this backdrop, which is the southern view). Stars rise on the
// left, arc over and set on the right about the south celestial pole below the horizon.
// Pure (node --test). Catalogue: starCatalog.mjs (Yale BSC5, vmag <= 4).
import { mulberry32 } from './rng.mjs';

export const OBSERVER = { latDeg: 52.23, lonDeg: 21.01 };
const D2R = Math.PI / 180;
const wrap360 = (d) => ((d % 360) + 360) % 360;

export function localSiderealDeg(ms, lonDeg = OBSERVER.lonDeg) {
  const jd = ms / 86400000 + 2440587.5;
  const d = jd - 2451545.0;
  return wrap360(280.46061837 + 360.98564736629 * d + lonDeg);
}

// Altitude above the horizon and azimuth from north through east, both in degrees.
export function altAz(raDeg, decDeg, lstDeg, latDeg = OBSERVER.latDeg) {
  const H = (lstDeg - raDeg) * D2R, dec = decDeg * D2R, lat = latDeg * D2R;
  const alt = Math.asin(Math.sin(dec) * Math.sin(lat) + Math.cos(dec) * Math.cos(lat) * Math.cos(H));
  // Meeus: azimuth measured westward from SOUTH; +180 turns it into from-north.
  const fromSouth = Math.atan2(Math.sin(H), Math.cos(H) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat));
  return { alt: alt / D2R, az: wrap360(fromSouth / D2R + 180) };
}

// The south-facing window: azimuth 90° (east) → u 0, 180° → 0.5, 270° (west) → u 1;
// altitude 0 → v 0 up to MAX_ALT → v 1. Anything outside is not drawn.
const MAX_ALT = 60;
const MAX_SHOWN_ALT = 62;
export function projectSouth(alt, az) {
  if (alt < 0 || alt > MAX_SHOWN_ALT || az < 90 || az > 270) return null;
  return { u: (az - 90) / 180, v: Math.min(alt, MAX_ALT) / MAX_ALT };
}

// The real moment the game's night shows: 210 s → 23:00 local, +1 h per 60 s, clamped at
// 01:00 (90 s). Two real hours round midnight, not a whole night: ten hours made the stars
// sweep ~17 px/s past someone standing still (owner 2026-11-02: "the stars are moving too
// fast"). Built from today's local 23:00 plus elapsed ms, so it always starts at 23:00 on the
// wall clock and spans exactly 2 h (no DST change falls between 23:00 and 01:00).
export const NIGHT_START_H = 23;
export const NIGHT_SPAN_H = 2;
export function skyDateFor(t, nowMs) {
  const since = (((t - 210) % 240) + 240) % 240;
  const hours = (Math.min(since, 120) / 120) * NIGHT_SPAN_H;
  const d = new Date(nowMs);
  const dusk = new Date(d.getFullYear(), d.getMonth(), d.getDate(), NIGHT_START_H).getTime();
  return dusk + hours * 3600 * 1000;
}

export function starsAt(ms, catalog) {
  const lst = localSiderealDeg(ms);
  const out = [];
  for (let i = 0; i < catalog.length; i++) {
    const [ra, dec] = catalog[i];
    const { alt, az } = altAz(ra, dec, lst);
    const p = projectSouth(alt, az);
    if (p) out.push({ u: p.u, v: p.v, i });
  }
  return out;
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
function bvColor(bv) {
  if (bv < 0) return 0xbfd4ff;
  if (bv < 0.6) return 0xffffff;
  if (bv < 1.2) return 0xfff0c8;
  return 0xffc49a;
}
export function starStyle(mag, bv) {
  return {
    r: clamp(2.6 - 0.45 * mag, 0.6, 2.8),
    alpha: clamp(1.15 - 0.18 * mag, 0.35, 1),
    color: mag < 1.5 ? bvColor(bv) : 0xffffff,
    // A Graphics circle is ~100 points plus a triangulation, redone every frame; the faint
    // majority are 1–2 px anyway, so they are squares (two triangles). Only the ~20 brightest
    // stars stay round.
    shape: mag < 1.5 ? 'circle' : 'rect',
  };
}

export const SHOOTING_CHANCE = 0.5;
export const DARK_WINDOW = [5, 55];
export function shootingStarFor(night) {
  const r = mulberry32((Math.imul(night, 2654435761) ^ 0x5eed) >>> 0);
  if (r() >= SHOOTING_CHANCE) return null;
  return {
    t: DARK_WINDOW[0] + r() * (DARK_WINDOW[1] - DARK_WINDOW[0]),
    u: 0.2 + 0.6 * r(),
    v: 0.55 + 0.35 * r(),
    angleDeg: 25 + 130 * r(),      // screen angle, y down: always heading downward
    length: 0.08 + 0.08 * r(),     // in u units
    durationMs: 800,
  };
}

// Fire when the clock crosses the moment in one small step; a jump of more than 5 s (a tab
// that was hidden) skips it rather than showing a stale meteor.
export function meteorDue(prevT, t, spec) {
  if (!spec || prevT === null || prevT === undefined) return false;
  if (t - prevT > 5 || t < prevT) return false;
  return prevT < spec.t && t >= spec.t;
}
